import React, { useId, useRef, useState } from 'react';
import Modal from '../orizon-ui/Modal';
import { useToast } from '../orizon-ui/Toast';
import { useConfirm } from '../orizon-ui/ConfirmDialog';
import { mapGruError } from '../../lib/services/gruService';

/** true se `valore` è diverso da com'era al primo render del form (per il "Scartare le modifiche?") */
export const useSporco = (valore) => {
  const iniziale = useRef(JSON.stringify(valore));
  return JSON.stringify(valore) !== iniziale.current;
};

/**
 * Gestione comune di salvataggio ed errori dei form:
 *  - errori: { [campo]: messaggio }  (campo come in mapGruError, es. 'key', 'attributi.diametro')
 *  - salva(fn, messaggioOk, onOk): esegue fn, mostra toast, mappa gli errori sul campo giusto.
 *    messaggioOk può essere una stringa o una funzione (risultatoDiFn) => stringa.
 *    onOk riceve il risultato di fn.
 */
export const useFormSalva = () => {
  const toast = useToast();
  const [salvando, setSalvando] = useState(false);
  const [errori, setErrori] = useState({});

  const impostaErrori = (nuovi) => setErrori(nuovi || {});
  const pulisci = (campo) => setErrori((prec) => (prec[campo] ? { ...prec, [campo]: null } : prec));

  const salva = async (fn, messaggioOk, onOk) => {
    setSalvando(true);
    try {
      const risultato = await fn();
      toast.success(typeof messaggioOk === 'function' ? messaggioOk(risultato) : messaggioOk);
      if (onOk) onOk(risultato);
      return true;
    } catch (e) {
      const err = mapGruError(e);
      if (err.campo) setErrori((prec) => ({ ...prec, [err.campo]: err.message }));
      toast.error(err.message);
      return false;
    } finally {
      setSalvando(false);
    }
  };

  return { salvando, errori, impostaErrori, pulisci, salva };
};

/**
 * Modal standard per i form: titolo, <form> con invio da tastiera, footer Annulla / Salva
 * e conferma "Scartare le modifiche?" se ci sono modifiche non salvate.
 */
const FormModal = ({
  titolo,
  etichetta,
  larghezza = 'lg',
  sporco = false,
  salvando = false,
  salvaLabel = 'Salva',
  annullaLabel = 'Annulla',
  onClose,
  onSubmit,
  children,
}) => {
  const confirm = useConfirm();
  const formId = useId();

  const richiediChiusura = async () => {
    if (salvando) return;
    if (sporco) {
      const ok = await confirm({
        titolo: 'Scartare le modifiche?',
        descrizione: 'I dati inseriti andranno persi',
        confermaLabel: 'Esci senza salvare',
      });
      if (!ok) return;
    }
    onClose();
  };

  return (
    <Modal
      isOpen
      onClose={richiediChiusura}
      titolo={titolo}
      etichetta={etichetta}
      larghezza={larghezza}
      bloccaChiusura={salvando}
      footer={(
        <>
          <button
            type="button"
            onClick={richiediChiusura}
            disabled={salvando}
            className="px-6 py-3 bg-muted text-foreground rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-muted/80 transition-all border border-border/40 disabled:opacity-50"
          >
            {annullaLabel}
          </button>
          <button
            type="submit"
            form={formId}
            disabled={salvando}
            className="px-8 py-3 bg-primary text-primary-foreground rounded-2xl text-[11px] font-black uppercase tracking-[.2em] hover:opacity-90 transition-all shadow-lg shadow-primary/20 disabled:opacity-50"
          >
            {salvando ? 'Salvataggio...' : salvaLabel}
          </button>
        </>
      )}
    >
      <form
        id={formId}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (!salvando) onSubmit();
        }}
        className="space-y-6"
      >
        {children}
      </form>
    </Modal>
  );
};

export default FormModal;
